import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Eye, FileText, Download } from "lucide-react";
import { NavLink } from "react-router-dom";
import { Permissions } from "../../../../permissions";
import { CircularProgress } from "@mui/material";

// Define the columns for the billing table
const columns = [
  { id: "date", label: "Date", minWidth: 120, required: true },
  { id: "invoiceNumber", label: "Invoice No.", minWidth: 140, required: true },
  { id: "billAmount", label: "Bill Amount", minWidth: 130, required: true },
  { id: "outstanding", label: "Outstanding", minWidth: 130, required: true },
  {
    id: "adjustedAmount",
    label: "Adjusted Amt.",
    minWidth: 130,
    required: false,
  },
  { id: "status", label: "Status", minWidth: 120, required: true },
  { id: "action", label: "Action", minWidth: 100, required: true },
];

// Sample data
const createData = (
  date,
  invoiceNumber,
  billAmount,
  outstanding,
  adjustedAmount,
  status,
) => {
  return {
    date,
    invoiceNumber,
    billAmount,
    outstanding,
    adjustedAmount,
    status,
  };
};

const sampleData = [
  createData("12/03/2024", "INV-12072023", 35000, 12998, 1500, "Paid"),
  createData("10/03/2024", "INV-10072023", 28500, 0, 0, "Paid"),
  createData("05/03/2024", "INV-05072023", 42000, 15000, 3000, "Pending"),
  createData("28/02/2024", "INV-28062023", 18500, 0, 0, "Paid"),
  createData("20/02/2024", "INV-20062023", 53000, 25000, 4000, "Overdue"),
  createData("15/02/2024", "INV-15062023", 31000, 0, 0, "Paid"),
  createData("08/02/2024", "INV-08062023", 22500, 0, 0, "Paid"),
  createData("01/02/2024", "INV-01062023", 45000, 0, 0, "Paid"),
  createData("25/01/2024", "INV-25052023", 36000, 0, 0, "Paid"),
  createData("18/01/2024", "INV-18052023", 29500, 0, 0, "Paid"),
  createData("10/01/2024", "INV-10052023", 38000, 0, 0, "Paid"),
  createData("03/01/2024", "INV-03052023", 41500, 0, 0, "Paid"),
  createData("26/12/2023", "INV-26042023", 27000, 0, 0, "Paid"),
  createData("19/12/2023", "INV-19042023", 33500, 0, 0, "Paid"),
  createData("12/12/2023", "INV-12042023", 48000, 0, 0, "Paid"),
];

export default function BillingTable() {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [isLoading, setIsLoading] = React.useState(false);

  // Check permissions
  const canDownloadInvoice = Permissions.find(permission =>
    permission.includes("download_customerBilling"),
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Format currency
  const formatCurrency = amount => {
    return `₹ ${amount.toLocaleString("en-IN")}`;
  };

  // Get status class
  const getStatusClass = status => {
    switch (status) {
      case "Paid":
        return "bg-green-100 text-green-800";
      case "Pending":
        return "bg-amber-100 text-amber-800";
      case "Overdue":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        boxShadow: "none",
        border: "1px solid #E5E7EB",
        borderRadius: "0.5rem",
      }}>
      <TableContainer
        sx={{ maxHeight: "calc(100vh - 450px)" }}
        className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell
                  key={column.id}
                  align="center"
                  sx={{
                    minWidth: column.minWidth,
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <div className="flex items-center justify-center py-4">
                    <CircularProgress size={40} />
                  </div>
                </TableCell>
              </TableRow>
            ) : sampleData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <div className="py-4 text-gray-500 font-outfit">
                    No billing records found
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              sampleData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={index}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                      }}
                      className="font-outfit">
                      {row.date}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                      }}
                      className="font-outfit">
                      {row.invoiceNumber}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                      }}
                      className="font-outfit">
                      {formatCurrency(row.billAmount)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                      }}
                      className="font-outfit">
                      {formatCurrency(row.outstanding)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                      }}
                      className="font-outfit">
                      {formatCurrency(row.adjustedAmount)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                      }}
                      className="font-outfit">
                      <div
                        className={`px-3 py-1 rounded-full text-sm font-medium inline-block ${getStatusClass(
                          row.status,
                        )}`}>
                        {row.status}
                      </div>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                      }}
                      className="font-outfit">
                      <div className="flex items-center justify-center gap-3">
                        <NavLink
                          to={`/billing/invoice/${row.invoiceNumber}`}
                          className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors">
                          <Eye size={16} className="text-[#5652B0]" />
                        </NavLink>

                        {canDownloadInvoice && (
                          <button className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors">
                            <Download size={16} className="text-[#5652B0]" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={sampleData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: "1px solid #E5E7EB",
          ".MuiTablePagination-select": {
            borderRadius: "0.375rem",
          },
          fontFamily: "Outfit",
        }}
      />

      {isLoading && (
        <div className="absolute top-0 left-0 right-0">
          <div className="h-1 w-full overflow-hidden">
            <div className="animate-progress bg-[#5652B0] h-1"></div>
          </div>
        </div>
      )}
    </Paper>
  );
}
